
import React from 'react';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogClose 
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { VideoEvent } from '@/hooks/useVideoEvents';
import { X, PenIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useSubscriptionStatus } from '@/hooks/useSubscriptionStatus';
import { SubscriptionLock } from '@/components/subscription/SubscriptionLock';

// Import our refactored components
import StatsTab from './video-details/tabs/StatsTab';
import PropertiesTab from './video-details/tabs/PropertiesTab';
import ConversationTab from './video-details/tabs/ConversationTab';
import HistoryTab from './video-details/tabs/HistoryTab';
import VideoPreview from './video-details/VideoPreview';
import DialogFooter from './video-details/DialogFooter';

interface VideoDetailsDialogProps {
  video: VideoEvent | null;
  isOpen: boolean;
  onClose: () => void;
}

const VideoDetailsDialog: React.FC<VideoDetailsDialogProps> = ({ 
  video, 
  isOpen, 
  onClose 
}) => {
  const { isSubscriptionActive } = useSubscriptionStatus();
  
  if (!video) return null;
  
  // Render locked content if no subscription
  if (!isSubscriptionActive) {
    return (
      <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <DialogContent 
          className="max-w-md p-0 bg-white rounded-md overflow-hidden" 
        >
          <DialogHeader className="p-4 border-b flex flex-row items-center justify-between">
            <DialogTitle className="text-lg font-medium">Publication</DialogTitle>
            <button 
              onClick={onClose}
              className="h-8 w-8 flex items-center justify-center rounded-full hover:bg-gray-100"
            >
              <X className="h-4 w-4" />
            </button>
          </DialogHeader>
          
          <div className="p-6">
            <SubscriptionLock 
              title="Détails verrouillés"
              description="Les détails des publications ne sont disponibles qu'avec un abonnement actif."
              buttonText="Débloquer l'accès"
            />
          </div>
        </DialogContent>
      </Dialog>
    );
  }
  
  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent 
        className="max-w-4xl p-0 bg-white rounded-md overflow-hidden" 
        style={{ paddingRight: 0 }}
      >
        {/* Custom header with our own close button */}
        <DialogHeader className="p-4 border-b flex flex-row items-center justify-between">
          <DialogTitle className="text-lg font-medium">Publication</DialogTitle>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" className="h-8">
              <PenIcon size={16} />
            </Button>
            <button 
              onClick={onClose}
              className="h-8 w-8 flex items-center justify-center rounded-full hover:bg-gray-100"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </DialogHeader>
        
        <div className="flex h-[80vh]">
          <div className="w-[60%] border-r overflow-y-auto">
            <Tabs defaultValue="stats" className="w-full">
              <TabsList className="w-full justify-start rounded-none border-b bg-white px-4">
                <TabsTrigger value="stats" className="data-[state=active]:border-b-2 data-[state=active]:border-orange-500 data-[state=active]:shadow-none rounded-none">
                  Rapports et statistiques ROI
                </TabsTrigger>
                <TabsTrigger value="properties" className="data-[state=active]:border-b-2 data-[state=active]:border-orange-500 data-[state=active]:shadow-none rounded-none">
                  Propriétés
                </TabsTrigger>
                <TabsTrigger value="conversation" className="data-[state=active]:border-b-2 data-[state=active]:border-orange-500 data-[state=active]:shadow-none rounded-none">
                  Conversation
                </TabsTrigger>
                <TabsTrigger value="history" className="data-[state=active]:border-b-2 data-[state=active]:border-orange-500 data-[state=active]:shadow-none rounded-none">
                  Historique
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="stats">
                <StatsTab />
              </TabsContent>
              
              <TabsContent value="properties">
                <PropertiesTab video={video} />
              </TabsContent>
              
              <TabsContent value="conversation">
                <ConversationTab />
              </TabsContent>
              
              <TabsContent value="history">
                <HistoryTab video={video} />
              </TabsContent>
            </Tabs>
          </div>
          
          <div className="w-[40%]">
            <VideoPreview video={video} />
          </div>
        </div>
        
        <DialogFooter />
      </DialogContent>
    </Dialog>
  );
};

export default VideoDetailsDialog;
